import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { MenuItem } from '@mui/material';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { HomepageCollectionCard, HomepageContent, HomepageProductRail, Product } from '../../types';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { resolveImageUrl } from '../../utils/image';

interface LinkOption {
  label: string;
  value: string;
}

const AdminStorefrontPage: React.FC = () => {
  const [content, setContent] = useState<HomepageContent | null>(null);
  const [linkOptions, setLinkOptions] = useState<LinkOption[]>([]);
  const [productOptions, setProductOptions] = useState<Product[]>([]);

  useEffect(() => {
    apiClient
      .get(API_ENDPOINTS.PRODUCTS, { params: { limit: 200 } })
      .then((res) => setProductOptions((res.data.products || []) as Product[]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiClient
      .get(API_ENDPOINTS.CATEGORIES)
      .then((res) => {
        const categories = (res.data as Array<{ name: string; slug?: string; _id: string }>) || [];
        setLinkOptions([
          { label: 'All products', value: '/products' },
          ...categories.map((c) => ({
            label: `Category: ${c.name}`,
            value: `/products?category=${c.slug || c._id}`
          })),
          { label: 'Under ₹399', value: '/products?maxPrice=399' },
          { label: 'Under ₹599', value: '/products?maxPrice=599' },
          { label: 'Under ₹999', value: '/products?maxPrice=999' }
        ]);
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.SITE_SETTINGS}/homepage`);
      setContent(response.data.homepage as HomepageContent);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load homepage content'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!content) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await apiClient.put(`${API_ENDPOINTS.SITE_SETTINGS}/homepage`, {
        homepage: content
      });
      setContent(response.data.homepage as HomepageContent);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to save homepage content'));
    } finally {
      setSaving(false);
    }
  };

  const setHero = (field: string, value: string) =>
    setContent((prev) => (prev ? { ...prev, hero: { ...prev.hero, [field]: value } } : prev));

  const setInfoCard = (id: string, field: string, value: string | boolean) =>
    setContent((prev) =>
      prev
        ? {
            ...prev,
            hero: {
              ...prev.hero,
              infoCards: prev.hero.infoCards.map((c) => (c.id === id ? { ...c, [field]: value } : c))
            }
          }
        : prev
    );

  const setPricePoint = (id: string, field: string, value: string | boolean) =>
    setContent((prev) =>
      prev
        ? {
            ...prev,
            pricePoints: prev.pricePoints.map((p) => (p.id === id ? { ...p, [field]: value } : p))
          }
        : prev
    );

  const setCollection = (id: string, patch: Partial<HomepageCollectionCard>) =>
    setContent((prev) =>
      prev
        ? {
            ...prev,
            collections: prev.collections.map((c) => (c.id === id ? { ...c, ...patch } : c))
          }
        : prev
    );

  const addCollection = () =>
    setContent((prev) =>
      prev
        ? {
            ...prev,
            collections: [
              ...prev.collections,
              {
                id: `collection-${Date.now()}`,
                eyebrow: 'Collections',
                title: 'New collection',
                body: '',
                link: '/products',
                image: null,
                visible: true,
                order: prev.collections.length + 1
              }
            ]
          }
        : prev
    );

  const removeCollection = (id: string) =>
    setContent((prev) =>
      prev ? { ...prev, collections: prev.collections.filter((c) => c.id !== id) } : prev
    );

  const moveCollection = (id: string, direction: -1 | 1) =>
    setContent((prev) => {
      if (!prev) return prev;
      const sorted = [...prev.collections].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((c) => c.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= sorted.length) return prev;
      [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
      return { ...prev, collections: sorted.map((c, i) => ({ ...c, order: i + 1 })) };
    });

  const rails = content?.productRails || [];

  const setRail = (id: string, patch: Partial<HomepageProductRail>) =>
    setContent((prev) =>
      prev
        ? {
            ...prev,
            productRails: (prev.productRails || []).map((r) => (r.id === id ? { ...r, ...patch } : r))
          }
        : prev
    );

  const addRail = () =>
    setContent((prev) =>
      prev
        ? {
            ...prev,
            productRails: [
              ...(prev.productRails || []),
              {
                id: `rail-${Date.now()}`,
                title: 'New product rail',
                subtitle: '',
                source: 'manual' as const,
                productIds: [],
                viewAllLink: '',
                limit: 8,
                visible: true,
                order: (prev.productRails || []).length + 1
              }
            ]
          }
        : prev
    );

  const removeRail = (id: string) =>
    setContent((prev) =>
      prev ? { ...prev, productRails: (prev.productRails || []).filter((r) => r.id !== id) } : prev
    );

  const moveRail = (id: string, direction: -1 | 1) =>
    setContent((prev) => {
      if (!prev) return prev;
      const sorted = [...(prev.productRails || [])].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((r) => r.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= sorted.length) return prev;
      [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
      return { ...prev, productRails: sorted.map((r, i) => ({ ...r, order: i + 1 })) };
    });

  const uploadCollectionImage = async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('images', file);
    try {
      const response = await apiClient.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = response.data?.urls?.[0] || response.data?.[0];
      if (url) setCollection(id, { image: url });
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Image upload failed'));
    }
  };

  if (loading || !content) {
    return (
      <Box>
        <Skeleton width={280} height={40} />
        <Skeleton variant="rounded" height={240} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={240} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Homepage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Everything customers see on vediluxe.com's landing page
          </Typography>
        </Box>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save & publish'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Homepage updated — changes are live.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Hero section
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="Eyebrow (small text above headline)" value={content.hero.eyebrow} onChange={(e) => setHero('eyebrow', e.target.value)} />
          <TextField
            label="Headline"
            value={content.hero.headline}
            onChange={(e) => setHero('headline', e.target.value)}
            multiline
            minRows={2}
            helperText="Line breaks are kept — press Enter for a two-line headline"
          />
          <TextField label="Subheadline" value={content.hero.subheadline} onChange={(e) => setHero('subheadline', e.target.value)} multiline minRows={2} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField label="Primary button label" value={content.hero.primaryCtaLabel} onChange={(e) => setHero('primaryCtaLabel', e.target.value)} />
            <TextField label="Primary button link" value={content.hero.primaryCtaLink} onChange={(e) => setHero('primaryCtaLink', e.target.value)} />
            <TextField label="Secondary button label" value={content.hero.secondaryCtaLabel} onChange={(e) => setHero('secondaryCtaLabel', e.target.value)} />
            <TextField label="Secondary button link" value={content.hero.secondaryCtaLink} onChange={(e) => setHero('secondaryCtaLink', e.target.value)} />
          </Box>
        </Box>

        <Divider sx={{ my: 2.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Side cards
        </Typography>
        {content.hero.infoCards.map((card) => (
          <Box key={card.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 1.5, mb: 1.5, alignItems: 'start' }}>
            <TextField size="small" label="Title" value={card.title} onChange={(e) => setInfoCard(card.id, 'title', e.target.value)} />
            <TextField size="small" label="Text" value={card.body} onChange={(e) => setInfoCard(card.id, 'body', e.target.value)} multiline />
            <Tooltip title={card.visible ? 'Visible' : 'Hidden'}>
              <Switch checked={card.visible} onChange={(e) => setInfoCard(card.id, 'visible', e.target.checked)} />
            </Tooltip>
          </Box>
        ))}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Price-point cards
        </Typography>
        {content.pricePoints.map((point) => (
          <Box key={point.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.4fr auto', gap: 1.5, mb: 1.5, alignItems: 'start' }}>
            <TextField size="small" label="Label" value={point.label} onChange={(e) => setPricePoint(point.id, 'label', e.target.value)} />
            <TextField size="small" label="Text" value={point.body} onChange={(e) => setPricePoint(point.id, 'body', e.target.value)} />
            <TextField size="small" label="Link" value={point.link} onChange={(e) => setPricePoint(point.id, 'link', e.target.value)} />
            <Switch checked={point.visible} onChange={(e) => setPricePoint(point.id, 'visible', e.target.checked)} />
          </Box>
        ))}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Collection cards
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addCollection}>
            Add card
          </Button>
        </Box>
        {[...content.collections]
          .sort((a, b) => a.order - b.order)
          .map((card, index, arr) => (
            <Paper key={card.id} variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#FCFAFC' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Card {index + 1}
                </Typography>
                <Box>
                  <IconButton size="small" disabled={index === 0} onClick={() => moveCollection(card.id, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={index === arr.length - 1} onClick={() => moveCollection(card.id, 1)}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <Switch size="small" checked={card.visible} onChange={(e) => setCollection(card.id, { visible: e.target.checked })} />
                  <IconButton size="small" color="error" onClick={() => removeCollection(card.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <TextField size="small" label="Eyebrow" value={card.eyebrow} onChange={(e) => setCollection(card.id, { eyebrow: e.target.value })} />
                <TextField size="small" label="Title" value={card.title} onChange={(e) => setCollection(card.id, { title: e.target.value })} />
                <TextField size="small" label="Description" value={card.body} onChange={(e) => setCollection(card.id, { body: e.target.value })} sx={{ gridColumn: '1 / -1' }} multiline />
                <Autocomplete
                  size="small"
                  freeSolo
                  options={linkOptions}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.value
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.value}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  value={card.link}
                  onChange={(_, option) =>
                    setCollection(card.id, {
                      link: typeof option === 'string' ? option : option?.value || ''
                    })
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') setCollection(card.id, { link: value });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link"
                      helperText="Pick a category, or type a custom path"
                    />
                  )}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {card.image ? (
                    <Box component="img" src={resolveImageUrl(card.image)} alt="" sx={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                  ) : (
                    <ImageOutlinedIcon color="disabled" />
                  )}
                  <Button size="small" component="label">
                    {card.image ? 'Change image' : 'Add image'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadCollectionImage(card.id, file);
                      }}
                    />
                  </Button>
                  {card.image && (
                    <Button size="small" color="inherit" onClick={() => setCollection(card.id, { image: null })}>
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Product rails
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addRail}>
            Add rail
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rows of products on the homepage. &quot;By sales&quot; ranks from real orders (newest
          products fill in while sales data is thin), &quot;Newest first&quot; shows latest arrivals,
          &quot;Hand-picked&quot; lets you choose exact products.
        </Typography>
        {[...rails]
          .sort((a, b) => a.order - b.order)
          .map((rail, index, arr) => (
            <Paper key={rail.id} variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#FCFAFC' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Rail {index + 1}
                </Typography>
                <Box>
                  <IconButton size="small" disabled={index === 0} onClick={() => moveRail(rail.id, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={index === arr.length - 1} onClick={() => moveRail(rail.id, 1)}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title={rail.visible ? 'Visible' : 'Hidden'}>
                    <Switch size="small" checked={rail.visible} onChange={(e) => setRail(rail.id, { visible: e.target.checked })} />
                  </Tooltip>
                  <IconButton size="small" color="error" onClick={() => removeRail(rail.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <TextField size="small" label="Title" value={rail.title} onChange={(e) => setRail(rail.id, { title: e.target.value })} />
                <TextField size="small" label="Subtitle" value={rail.subtitle} onChange={(e) => setRail(rail.id, { subtitle: e.target.value })} />
                <TextField
                  size="small"
                  select
                  label="Products come from"
                  value={rail.source}
                  onChange={(e) =>
                    setRail(rail.id, { source: e.target.value as HomepageProductRail['source'] })
                  }
                >
                  <MenuItem value="bestsellers">By sales (best sellers)</MenuItem>
                  <MenuItem value="newest">Newest first</MenuItem>
                  <MenuItem value="manual">Hand-picked</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="Max products"
                  value={rail.limit}
                  onChange={(e) =>
                    setRail(rail.id, { limit: Math.min(Math.max(Number(e.target.value) || 8, 1), 24) })
                  }
                  disabled={rail.source === 'manual'}
                  helperText={rail.source === 'manual' ? 'Hand-picked rails show every selected product' : undefined}
                />
                {rail.source === 'manual' && (
                  <Autocomplete
                    size="small"
                    multiple
                    options={productOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    value={rail.productIds
                      .map((pid) => productOptions.find((p) => p._id === pid))
                      .filter(Boolean) as Product[]}
                    onChange={(_, selected) =>
                      setRail(rail.id, { productIds: selected.map((p) => p._id) })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Products" helperText="Shown in this order" />
                    )}
                    sx={{ gridColumn: '1 / -1' }}
                  />
                )}
                <Autocomplete
                  size="small"
                  freeSolo
                  options={linkOptions}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.value}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  value={rail.viewAllLink}
                  onChange={(_, option) =>
                    setRail(rail.id, {
                      viewAllLink: typeof option === 'string' ? option : option?.value || ''
                    })
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') setRail(rail.id, { viewAllLink: value });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="View-all link (optional)"
                      helperText="Where the rail's 'View all' points; leave empty to hide it"
                    />
                  )}
                  sx={{ gridColumn: '1 / -1' }}
                />
              </Box>
            </Paper>
          ))}
      </Paper>

      <Button variant="contained" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save & publish'}
      </Button>
    </Box>
  );
};

export default AdminStorefrontPage;
